import React from 'react';
import PropTypes from 'prop-types';

import Box from 'web_component_library/box';
import RestrictedBox from 'web_component_library/restrictedBox';
import AutoplayCarousel from '../autoplayCarousel';
// eslint-disable-next-line import/no-cycle
import { componentMapping } from '../../../utils/helpers';

import {
  getTargetedComponent,
  getComponentToRender,
} from '../../../utils/targetHelpers';

const HERO_BANNER_RESTRICT_CONTENT_MAX_WIDTH = '2048px';

const defaultTargetAudience = '';
const HeroBanner = ({
  content = null,
  targetAudience = defaultTargetAudience,
  openPopup,
  promoBannerNoBottomSpace = false,
  ...props
}) => {
  // This props is being used when the user add a Carousel to a group row
  const propsCloned = { ...props };
  if (propsCloned) delete propsCloned?.page;

  const bannerProps = React.useMemo(() => {
    const contentOrPropsCloned = content || propsCloned;
    switch (contentOrPropsCloned?.identifier) {
      case 'targetedPromoBannerList':
        return getTargetedComponent(contentOrPropsCloned, targetAudience);
      case 'carousel':
        return {
          ...contentOrPropsCloned,
          components: contentOrPropsCloned.components.reduce(
            (acc, component) =>
              getComponentToRender(acc, component, targetAudience),
            [],
          ),
        };
      default:
        return contentOrPropsCloned ? { ...contentOrPropsCloned } : propsCloned;
    }
  }, [content, propsCloned, targetAudience]);

  if (bannerProps.identifier === 'carousel') {
    const isFullWidth = !bannerProps?.restrictCarouselContent;
    return (
      <Box
        maxWidth={isFullWidth ? HERO_BANNER_RESTRICT_CONTENT_MAX_WIDTH : '100%'}
        mx="auto"
      >
        <AutoplayCarousel
          {...bannerProps}
          isFullWidth={isFullWidth}
          promoBannerNoBottomSpace={promoBannerNoBottomSpace}
        >
          {bannerProps.components.reduce(
            (componentsToRender, componentProps) => {
              const Component = componentMapping[componentProps.identifier];

              const {
                areAllSlidesSameHeight,
                slideFixedHeight,
                slideMobileFixedHeight,
              } = bannerProps;

              const fixedHeight = areAllSlidesSameHeight
                ? `${slideFixedHeight || 350}px`
                : null;
              const fixedMobileHeight =
                areAllSlidesSameHeight && slideMobileFixedHeight
                  ? `${slideMobileFixedHeight}px`
                  : null;

              if (Component) {
                componentsToRender.push(
                  <Component
                    key={componentProps.id}
                    {...componentProps}
                    isHeroBanner
                    openPopup={openPopup}
                    fixedHeight={fixedHeight}
                    fixedMobileHeight={fixedMobileHeight}
                    borderRadius={isFullWidth ? '0' : undefined}
                  />,
                );
              }
              return componentsToRender;
            },
            [],
          )}
        </AutoplayCarousel>
      </Box>
    );
  }

  const HeroPromoBanner = componentMapping[bannerProps.identifier];
  if (!HeroPromoBanner) return null;

  const heroPromoBannerComponent = (
    <HeroPromoBanner
      {...bannerProps}
      isHeroBanner
      openPopup={openPopup}
      borderRadius={0}
    />
  );

  return bannerProps.appearance !== 'fullWidth' ? (
    <Box
      bg={bannerProps.textBlockBackground}
      sx={{
        paddingTop: '40px',
        paddingBottom: promoBannerNoBottomSpace ? '0px' : '40px',
        paddingLeft: 'md',
        paddingRight: 'md',
      }}
    >
      <RestrictedBox maxWidth={HERO_BANNER_RESTRICT_CONTENT_MAX_WIDTH}>
        {heroPromoBannerComponent}
      </RestrictedBox>
    </Box>
  ) : (
    <Box>{heroPromoBannerComponent}</Box>
  );
};

HeroBanner.propTypes = {
  content: PropTypes.shape({
    identifier: PropTypes.oneOf([
      'promoBanner',
      'promoBannerL',
      'targetedPromoBannerList',
      'carousel',
    ]),
    components: PropTypes.arrayOf(PropTypes.object),
  }),
  targetAudience: PropTypes.string,
  openPopup: PropTypes.func.isRequired,
  props: PropTypes.object,
  promoBannerNoBottomSpace: PropTypes.bool,
};

export default HeroBanner;
